import type { PlatformPlanFeatures } from "@/types";
import type { LucideIcon } from "lucide-react";
import { Bot, FileCheck, ShieldCheck, Video } from "lucide-react";

export const USER_PRODUCT_CODES = [
  "telehealth",
  "ai_notes",
  "rcm",
  "eligibility",
] as const;

export type UserProductCode = (typeof USER_PRODUCT_CODES)[number];

/** Core seat products shown in the main product grid (Tebra-style). */
export const USER_PRODUCT_CORE_CODES: UserProductCode[] = [
  "telehealth",
  "eligibility",
  "rcm",
];

/** Paid add-ons shown under a separate Add-ons heading. */
export const USER_PRODUCT_ADDON_CODES: UserProductCode[] = ["ai_notes"];

export const LEGACY_ALL_PRODUCT_CODES = new Set(["clinical", "practice"]);

export interface UserProductMeta {
  label: string;
  description: string;
  planFeature: keyof PlatformPlanFeatures;
  aiSeatNote?: string;
  icon: LucideIcon;
  /** Short label for card grids */
  shortLabel?: string;
}

export const USER_PRODUCT_META: Record<UserProductCode, UserProductMeta> = {
  telehealth: {
    label: "Telehealth",
    shortLabel: "Telehealth",
    description: "Join links and host virtual visits from the tracker and schedule",
    planFeature: "telehealth",
    icon: Video,
  },
  ai_notes: {
    label: "AI Note Assist & Copilot",
    shortLabel: "AI Assist",
    description: "AI scribe, note generation, and clinician copilot panel",
    planFeature: "ai_notes",
    aiSeatNote: "Also requires a licensed provider seat",
    icon: Bot,
  },
  rcm: {
    label: "Billing / Claims (RCM)",
    shortLabel: "Billing",
    description: "Submit claims through the clearinghouse on claim review",
    planFeature: "rcm",
    icon: FileCheck,
  },
  eligibility: {
    label: "Eligibility Checks",
    shortLabel: "Eligibility",
    description: "Run 270/271 eligibility from the patient tracker and claim review",
    planFeature: "eligibility",
    icon: ShieldCheck,
  },
};

export function hasLegacyAllProducts(products?: string[] | null): boolean {
  if (!products?.length) return false;
  return products.some((code) => LEGACY_ALL_PRODUCT_CODES.has(code));
}

/** Whether the user may use a service (matches backend HasUserProduct). */
export function hasUserProduct(
  products: string[] | undefined | null,
  code: UserProductCode,
): boolean {
  if (hasLegacyAllProducts(products)) {
    return true;
  }
  if (!products?.length) {
    return false;
  }
  return products.includes(code);
}

/** Canonical enabled codes for display. */
export function explicitUserProducts(
  products: string[] | undefined | null,
): UserProductCode[] {
  if (hasLegacyAllProducts(products)) {
    return [...USER_PRODUCT_CODES];
  }
  if (!products?.length) {
    return [];
  }
  return USER_PRODUCT_CODES.filter((code) => products.includes(code));
}

export function userProductLabel(code: string): string {
  if (code in USER_PRODUCT_META) {
    return USER_PRODUCT_META[code as UserProductCode].label;
  }
  return code.replace(/_/g, " ");
}

export function isOrgPlanFeatureEnabled(
  features: PlatformPlanFeatures | undefined,
  code: UserProductCode,
  enabledAddons?: string[] | null,
): boolean {
  if (!features) return true;
  const key = USER_PRODUCT_META[code].planFeature;
  return Boolean(features[key]) || Boolean(enabledAddons?.includes(code));
}

export function productsFromToggles(
  toggles: Record<UserProductCode, boolean>,
): string[] {
  return USER_PRODUCT_CODES.filter((code) => toggles[code]);
}

export function togglesFromProducts(
  products: string[] | undefined | null,
): Record<UserProductCode, boolean> {
  const enabled = explicitUserProducts(products);
  return USER_PRODUCT_CODES.reduce(
    (acc, code) => {
      acc[code] = enabled.includes(code);
      return acc;
    },
    {} as Record<UserProductCode, boolean>,
  );
}

export function isServiceNotEnabledError(error: unknown): boolean {
  const data = (error as { response?: { data?: { error?: string } } })?.response
    ?.data;
  return data?.error === "service_not_enabled";
}

export function serviceNotEnabledMessage(error: unknown): string | null {
  const data = (error as { response?: { data?: { message?: string } } })
    ?.response?.data;
  return data?.message ?? null;
}
